import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { cn } from "@/lib/utils";
import { ArrowRight, CheckCircle, Code, Copy, ExternalLink, Globe, Server } from "lucide-react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";
import { trackPromotionalFunnelEvent } from "../lib/analytics";

interface DocSection {
  id: string;
  titleKey: string;
  contentKey: string;
}

const Document = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState<string>("introduction");
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const apiBaseUrl = "https://api.gugudata.ai/api/v1";
  const streamEndpoint = `${apiBaseUrl}/stream`;

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(id);
      setTimeout(() => setCopySuccess(null), 2000);
    });
  };

  // 文档内容
  const sections: DocSection[] = [
    {
      id: "introduction",
      titleKey: "documentation.introduction",
      contentKey: "documentation.introduction.content",
    },
    {
      id: "api",
      titleKey: "documentation.api",
      contentKey: "documentation.api.content",
    },
  ];

  const trackContactClick = (surface: string, ctaName: string) => {
    trackPromotionalFunnelEvent("click_contact_sales", {
      surface,
      cta_name: ctaName,
      funnel_stage: "documentation_to_contact",
      result_status: "clicked",
    });
  };

  const apiExample = `curl ${streamEndpoint} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <GuGuData AI API Key>" \\
  -d '{
        "query": "What is the next solar term in the 24 solar terms?",
        "variables": {},
        "plugins": ["education", "finance", "lunar"]
      }'`;

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{t("meta.documentation.title")}</title>
        <meta name="description" content={t("meta.documentation.description")} />
        <meta name="keywords" content={t("meta.documentation.keywords")} />
        <meta property="og:title" content={t("meta.documentation.title")} />
        <meta property="og:description" content={t("meta.documentation.description")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gugudata.ai/document" />
        <meta property="og:image" content="https://www.gugudata.ai/og.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="GuGuData.AI" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("meta.documentation.title")} />
        <meta name="twitter:description" content={t("meta.documentation.description")} />
        <meta name="twitter:image" content="https://www.gugudata.ai/og.png" />
        <link rel="canonical" href="https://www.gugudata.ai/document" />
        <html lang={language} />
      </Helmet>

      <Navbar />
      <main className="flex-grow pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row gap-8">
            {/* 侧边导航 */}
            <div className="w-full md:w-64 flex-shrink-0">
              <nav className="sticky top-24">
                <h2 className="text-lg font-semibold mb-4">{t("documentation.title")}</h2>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => setActiveSection(section.id)}
                        className={cn(
                          "w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center",
                          activeSection === section.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                        )}
                      >
                        {activeSection === section.id && <ArrowRight className="mr-2 h-4 w-4" />}
                        {t(section.titleKey)}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* 主要内容区域 */}
            <div className="flex-1">
              <article className="prose prose-lg max-w-none dark:prose-invert">
                {activeSection === "introduction" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.introduction")}</h1>

                    <div className="mb-6">
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mr-2 mb-2">MCP Protocol</span>
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 mr-2 mb-2">
                        AI Data Integration
                      </span>
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 mb-2">API</span>
                    </div>

                    <p className="text-lg mb-6">{t("documentation.introduction.welcome")}</p>

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 mb-8">
                      <div className="flex items-start mb-4">
                        <Server className="w-6 h-6 text-brand-blue mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h3 className="text-xl font-semibold mb-2">{t("documentation.api.baseurl.title")}</h3>
                          <div className="flex items-center bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-md font-mono text-sm">
                            {apiBaseUrl}
                            <button
                              onClick={() => copyToClipboard(apiBaseUrl, "baseurl")}
                              className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                              aria-label="Copy to clipboard"
                            >
                              {copySuccess === "baseurl" ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h2 className="text-2xl font-semibold mb-4">{t("documentation.introduction.how_it_works.title")}</h2>
                    <p className="mb-6">{t("documentation.introduction.how_it_works.description")}</p>

                    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl mb-8">
                      <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center">
                          <Code className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
                          <span className="font-medium">{t("documentation.api.example.title")}</span>
                        </div>
                        <button
                          onClick={() => copyToClipboard(apiExample, "apiExample")}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                          aria-label="Copy to clipboard"
                        >
                          {copySuccess === "apiExample" ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </button>
                      </div>
                      <pre className="bg-gray-900 text-gray-100 p-6 rounded-b-xl overflow-x-auto text-sm">
                        <code>{apiExample}</code>
                      </pre>
                    </div>

                    <h2 className="text-2xl font-semibold mb-4">{t("documentation.introduction.mcp_plugins.title")}</h2>
                    <p className="mb-4">{t("documentation.introduction.mcp_plugins.description")}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-blue-600 dark:text-blue-400">{t("plugins.text.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.text.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-indigo-600 dark:text-indigo-400">{t("plugins.image.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.image.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-red-600 dark:text-red-400">{t("plugins.news.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.news.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-green-600 dark:text-green-400">{t("plugins.finance.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.finance.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-amber-600 dark:text-amber-400">{t("plugins.business.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.business.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-teal-600 dark:text-teal-400">{t("plugins.location.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.location.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-orange-600 dark:text-orange-400">{t("plugins.webtools.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.webtools.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-violet-600 dark:text-violet-400">{t("plugins.ai.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.ai.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-fuchsia-600 dark:text-fuchsia-400">{t("plugins.barcode.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.barcode.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-pink-600 dark:text-pink-400">{t("plugins.sms.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.sms.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-cyan-600 dark:text-cyan-400">{t("plugins.weather.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.weather.description")}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                        <h3 className="font-medium text-lg mb-2 text-emerald-600 dark:text-emerald-400">{t("plugins.education.title")}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("plugins.education.description")}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                      <a
                        href="https://www.gugudata.ai/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center">
                          <Globe className="w-5 h-5 mr-3 text-red-500" />
                          <div>
                            <div className="font-medium">{t("documentation.introduction.mcp_plugins.chinese")}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">https://www.gugudata.ai</div>
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </a>

                      <a
                        href="https://www.gugudata.ai/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center">
                          <Globe className="w-5 h-5 mr-3 text-blue-500" />
                          <div>
                            <div className="font-medium">{t("documentation.introduction.mcp_plugins.international")}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">https://www.gugudata.ai</div>
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </a>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 p-4 mb-8">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-blue-700 dark:text-blue-300">{t("documentation.introduction.note")}</p>
                        </div>
                      </div>
                    </div>

                  </div>
                )}

                {activeSection === "api" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.api")}</h1>

                    <div className="mb-6">
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mr-2 mb-2">REST API</span>
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 mr-2 mb-2">JSON</span>
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 mb-2">
                        Bearer Authentication
                      </span>
                    </div>

                    <p className="mb-6">{t("documentation.api.content")}</p>

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 mb-8">
                      <div className="flex items-start mb-4">
                        <Server className="w-6 h-6 text-brand-blue mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h3 className="text-xl font-semibold mb-2">{t("documentation.api.baseurl.title")}</h3>
                          <div className="flex items-center bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-md font-mono text-sm">
                            {apiBaseUrl}
                            <button
                              onClick={() => copyToClipboard(apiBaseUrl, "apiBaseUrl")}
                              className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                              aria-label="Copy to clipboard"
                            >
                              {copySuccess === "apiBaseUrl" ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h2 className="text-2xl font-semibold mb-4">{t("documentation.api.authentication.title")}</h2>
                    <p className="mb-6">{t("documentation.api.authentication.description")}</p>

                    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl mb-8">
                      <div className="px-6 py-4">
                        <h3 className="font-medium mb-2">{t("documentation.api.authentication.header")}</h3>
                        <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-md font-mono text-sm mb-4">Authorization: Bearer &lt;GuGuData AI API Key&gt;</div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("documentation.api.authentication.note")}</p>
                      </div>
                    </div>

                    <h2 className="text-2xl font-semibold mb-4">{t("documentation.api.endpoints.title")}</h2>

                    <div className="space-y-6 mb-8">
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 mr-3">POST</span>
                            <span className="font-mono text-sm">/stream</span>
                          </div>
                        </div>
                        <div className="px-6 py-4">
                          <h3 className="font-medium mb-2">{t("documentation.api.endpoints.chat_completions.title")}</h3>
                          <p className="text-gray-600 dark:text-gray-400 mb-4">{t("documentation.api.endpoints.chat_completions.description")}</p>

                          <h4 className="font-medium mb-2">{t("documentation.api.endpoints.chat_completions.params.title")}</h4>
                          <div className="overflow-x-auto mb-4">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("documentation.api.endpoints.parameter")}
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("documentation.api.endpoints.type")}
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("documentation.api.endpoints.required")}
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("documentation.api.endpoints.description")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                                <tr>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">query</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">string</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">{t("documentation.api.endpoints.yes")}</td>
                                  <td className="px-4 py-3 text-sm">{t("documentation.api.endpoints.chat_completions.params.model")}</td>
                                </tr>
                                <tr>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">variables</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">object</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">{t("documentation.api.endpoints.no")}</td>
                                  <td className="px-4 py-3 text-sm">{t("documentation.api.endpoints.chat_completions.params.messages")}</td>
                                </tr>
                                <tr>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">plugins</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">array</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">{t("documentation.api.endpoints.no")}</td>
                                  <td className="px-4 py-3 text-sm">{t("documentation.api.endpoints.chat_completions.params.stream")}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          <h4 className="font-medium mb-2">{t("documentation.api.endpoints.example_request")}</h4>
                          <div className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto text-sm mb-4">
                            <pre>
                              <code>{apiExample}</code>
                            </pre>
                          </div>

                          <h4 className="font-medium mb-2">{t("documentation.api.endpoints.example_response")}</h4>
                          <div className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                            <pre>
                              <code>{`data: {"content":"The next solar term in the 24 solar terms is Minor Heat"}
data: {"content":" (小暑, Xiǎoshǔ), which usually begins around July 7th."}`}</code>
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === "getting-started" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.getting-started")}</h1>
                    <p>{t("documentation.getting-started.content")}</p>
                  </div>
                )}

                {activeSection === "features" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.features")}</h1>
                    <p>{t("documentation.features.content")}</p>
                  </div>
                )}

                {activeSection === "examples" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.examples")}</h1>
                    <p>{t("documentation.examples.content")}</p>
                  </div>
                )}

                {activeSection === "troubleshooting" && (
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-4">{t("documentation.troubleshooting")}</h1>
                    <p>{t("documentation.troubleshooting.content")}</p>
                  </div>
                )}

                {/* Navigation Links */}
                <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="flex gap-4">
                      <a
                        href="/"
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-blue bg-blue-50 dark:bg-blue-900/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        ← Back to Home
                      </a>
                      <a
                        href="/contact"
                        onClick={() => trackContactClick("document_footer", "contact_support")}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        Contact Support
                      </a>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Need help?{" "}
                      <a href="/contact" onClick={() => trackContactClick("document_footer", "contact_team")} className="text-brand-blue hover:underline">
                        Contact our team
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Document;
